/**
 * Game Pre-fetch Hook
 * Pre-fetches games on initial load for smooth search experience
 * Uses React Query for caching and background fetching
 */

import { useQueryClient } from '@tanstack/react-query';
import { fetchGisGames } from '@/services/api/gamesService';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isMobile } from '@/utils/device';

// Default providers to pre-fetch (most popular ones)
const PREFETCH_PROVIDERS = [
  'Spribe',
  'PragmaticPlay',
  'NetEnt',
  'Playtech',
  'BGaming',
  'Ezugi',
  'Evolution',
  'PlayNGo',
  'Quickspin',
  'Yggdrasil'
];

const GAMES_PER_PAGE = 21;

/**
 * Pre-fetch games for search modal
 * Runs in background after initial page load
 */
// Global flag to ensure pre-fetch only happens once across entire app
let globalHasPrefetched = false;

export const useGamePrefetch = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const hasPrefetched = useRef(false);
  const prefetchTimeoutRef = useRef(null);

  useEffect(() => {
    // Don't prefetch on affiliate, settings, or other non-game pages
    const pathname = location.pathname;
    const skipPages = [
      '/affiliate',
      '/settings',
      '/provably-fair',
      '/promotions',
      '/vip-club',
      '/bonus-page',
      '/support',
      '/gift-cards',
    ];
    
    if (skipPages.some(page => pathname.startsWith(page))) {
      return; // Skip prefetch on these pages
    }

    // Only pre-fetch once globally (across entire app lifecycle)
    if (hasPrefetched.current || globalHasPrefetched) return;

    // Delay pre-fetch to not block initial render
    // On mobile, wait longer to ensure smooth initial load
    const delay = isMobile() ? 2000 : 1000;

    prefetchTimeoutRef.current = setTimeout(() => {
      hasPrefetched.current = true;
      globalHasPrefetched = true; // Mark as prefetched globally
      
      // Pre-fetch games from popular providers
      // Use low priority by batching requests
      const isMobileDevice = isMobile();
      const batchSize = isMobileDevice ? 2 : 3; // Smaller batches on mobile
      
      PREFETCH_PROVIDERS.forEach((provider, index) => {
        // Stagger requests to avoid overwhelming the device
        const staggerDelay = Math.floor(index / batchSize) * 500;
        
        setTimeout(() => {
          // Pre-fetch first page of games for each provider
          // Use low priority by setting it in the background
          queryClient.prefetchQuery({
            queryKey: ['gisGames', provider, 1],
            queryFn: async () => {
              const result = await fetchGisGames(provider, 1, GAMES_PER_PAGE);
              return {
                games: result.games || [],
                pagination: result.pagination || {
                  current_page: 1,
                  total_pages: 1,
                  total: result.games?.length || 0
                }
              };
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
          }).catch(error => {
            // Silently fail - pre-fetch shouldn't break the app
            if (process.env.NODE_ENV === 'development') {
              console.debug(`Pre-fetch failed for ${provider}:`, error);
            }
          });
        }, staggerDelay);
      });
    }, delay);

    // Cleanup
    return () => {
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current);
      }
    };
  }, [queryClient, location.pathname]);
};
